import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Phone,
  MapPin,
  Calendar,
  Search,
  Eye,
  X,
  User,
  CreditCard,
  RefreshCw,
  Printer,
} from "lucide-react";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "#d97706", bgColor: "#fef3c7" },
  { value: "pagado_pendiente_entrega", label: "Pagado", color: "#2563eb", bgColor: "#dbeafe" },
  { value: "confirmado", label: "En Preparación", color: "#4f46e5", bgColor: "#e0e7ff" },
  { value: "en_transito", label: "En Tránsito", color: "#9333ea", bgColor: "#f3e8ff" },
  { value: "entregado", label: "Entregado", color: "#16a34a", bgColor: "#dcfce7" },
  { value: "cancelado", label: "Cancelado", color: "#dc2626", bgColor: "#fee2e2" },
];

export default function CasaMatrizPedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroFecha, setFiltroFecha] = useState(new Date().toISOString().split("T")[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [updatingEstado, setUpdatingEstado] = useState(false);

  useEffect(() => {
    fetchPedidos();
  }, [filtroEstado]);

  const fetchPedidos = async () => {
    try {
      const params = filtroEstado ? `?estado=${filtroEstado}` : "";
      const res = await api.get(`/pedidos-terceros${params}`);
      setPedidos(res.data);
    } catch (error) {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  const updateEstado = async (pedidoId, nuevoEstado) => {
    setUpdatingEstado(true);
    try {
      await api.put(`/pedidos-terceros/${pedidoId}/estado`, { estado: nuevoEstado });
      toast.success(`Estado actualizado`);
      fetchPedidos();
      if (selectedPedido?.id === pedidoId) {
        const updated = await api.get(`/pedidos-terceros/${pedidoId}`);
        setSelectedPedido(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setUpdatingEstado(false);
    }
  };

  const getEstadoInfo = (estado) => {
    return ESTADOS.find(e => e.value === estado) || ESTADOS[0];
  };

  const getNextEstados = (estadoActual) => {
    const flujo = {
      "pendiente": ["confirmado", "cancelado"],
      "pagado_pendiente_entrega": ["confirmado", "cancelado"],
      "confirmado": ["en_transito", "cancelado"],
      "en_transito": ["entregado"],
      "entregado": [],
      "cancelado": [],
    };
    return flujo[estadoActual] || [];
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Filter by date and search
  const filteredPedidos = pedidos
    .filter(p => !filtroFecha || p.fecha_entrega === filtroFecha)
    .filter(p =>
      p.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.cliente_telefono?.includes(searchTerm)
    );

  // Group by status for Kanban-like view
  const pedidosPorEstado = {
    pendientes: filteredPedidos.filter(p => p.estado === "pendiente" || p.estado === "pagado_pendiente_entrega"),
    preparacion: filteredPedidos.filter(p => p.estado === "confirmado"),
    transito: filteredPedidos.filter(p => p.estado === "en_transito"),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-purple-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="casa-matriz-pedidos">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Gestión de Pedidos</h1>
          <p className="text-torta-muted">Confirma, prepara y despacha los pedidos</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={filtroFecha}
            onChange={(e) => setFiltroFecha(e.target.value)}
            className="w-40"
          />
          <Button onClick={fetchPedidos} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar cliente o teléfono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Kanban-style columns */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Por Confirmar */}
        <div className="bg-amber-50 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-medium text-amber-800">Por Confirmar ({pedidosPorEstado.pendientes.length})</h3>
          </div>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {pedidosPorEstado.pendientes.map(pedido => (
              <PedidoCard
                key={pedido.id}
                pedido={pedido}
                onView={() => setSelectedPedido(pedido)}
                onUpdateEstado={updateEstado}
                nextEstados={getNextEstados(pedido.estado)}
                updating={updatingEstado}
              />
            ))}
            {pedidosPorEstado.pendientes.length === 0 && (
              <p className="text-center text-amber-600 py-4 text-sm">Sin pedidos pendientes</p>
            )}
          </div>
        </div>

        {/* En Preparación */}
        <div className="bg-indigo-50 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
              <Package className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-medium text-indigo-800">En Preparación ({pedidosPorEstado.preparacion.length})</h3>
          </div>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {pedidosPorEstado.preparacion.map(pedido => (
              <PedidoCard
                key={pedido.id}
                pedido={pedido}
                onView={() => setSelectedPedido(pedido)}
                onUpdateEstado={updateEstado}
                nextEstados={getNextEstados(pedido.estado)}
                updating={updatingEstado}
              />
            ))}
            {pedidosPorEstado.preparacion.length === 0 && (
              <p className="text-center text-indigo-600 py-4 text-sm">Sin pedidos en preparación</p>
            )}
          </div>
        </div>

        {/* En Tránsito */}
        <div className="bg-purple-50 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-medium text-purple-800">En Tránsito ({pedidosPorEstado.transito.length})</h3>
          </div>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {pedidosPorEstado.transito.map(pedido => (
              <PedidoCard
                key={pedido.id}
                pedido={pedido}
                onView={() => setSelectedPedido(pedido)}
                onUpdateEstado={updateEstado}
                nextEstados={getNextEstados(pedido.estado)}
                updating={updatingEstado}
              />
            ))}
            {pedidosPorEstado.transito.length === 0 && (
              <p className="text-center text-purple-600 py-4 text-sm">Sin pedidos en tránsito</p>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedPedido && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-display text-lg text-torta-dark">Detalle del Pedido</h2>
              <button onClick={() => setSelectedPedido(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Cliente */}
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="font-medium text-torta-dark">{selectedPedido.cliente_nombre}</p>
                <p className="text-sm text-torta-muted flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {selectedPedido.cliente_telefono}
                </p>
                <p className="text-sm text-torta-muted flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {selectedPedido.cliente_direccion}
                </p>
              </div>

              {/* Entrega */}
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-sm text-blue-700">
                  <strong>Entrega:</strong> {selectedPedido.fecha_entrega} - {selectedPedido.hora_entrega}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Recaudo:</strong> {selectedPedido.tipo_recaudo === "pago_vendedor" ? "Ya pagado" : "Contra entrega"}
                </p>
              </div>

              {/* Items */}
              <div>
                <p className="font-medium text-torta-dark mb-2">Productos</p>
                {selectedPedido.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                    <span>{item.cantidad}x {item.producto_nombre}</span>
                    <span className="font-medium">{formatCurrency(item.subtotal)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 font-display text-lg">
                  <span>Total</span>
                  <span className="text-torta-brown">{formatCurrency(selectedPedido.total)}</span>
                </div>
              </div>

              {/* Actions */}
              {getNextEstados(selectedPedido.estado).length > 0 && (
                <div className="flex gap-2 pt-2">
                  {getNextEstados(selectedPedido.estado).map((estado) => {
                    const info = getEstadoInfo(estado);
                    return (
                      <Button
                        key={estado}
                        onClick={() => {
                          updateEstado(selectedPedido.id, estado);
                          setSelectedPedido(null);
                        }}
                        disabled={updatingEstado}
                        className="flex-1"
                        style={{ 
                          backgroundColor: estado === "cancelado" ? "#dc2626" : info.color,
                          color: "white"
                        }}
                      >
                        {info.label}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-component for pedido card
function PedidoCard({ pedido, onView, onUpdateEstado, nextEstados, updating }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-white rounded-xl p-3 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-medium text-torta-dark text-sm">{pedido.cliente_nombre}</p>
          <p className="text-xs text-torta-muted">{pedido.hora_entrega}</p>
        </div>
        <button onClick={onView} className="text-gray-400 hover:text-gray-600">
          <Eye className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-torta-muted mb-2 truncate">
        <MapPin className="w-3 h-3 inline mr-1" />
        {pedido.cliente_direccion}
      </p>
      <div className="flex items-center justify-between">
        <span className="font-display text-sm text-torta-brown">{formatCurrency(pedido.total)}</span>
        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{pedido.total_unidades} und</span>
      </div>
      {nextEstados.length > 0 && (
        <div className="flex gap-1 mt-2">
          {nextEstados.filter(e => e !== "cancelado").map(estado => (
            <Button
              key={estado}
              size="sm"
              onClick={() => onUpdateEstado(pedido.id, estado)}
              disabled={updating}
              className="flex-1 text-xs h-7 bg-torta-brown hover:bg-torta-brown/90"
            >
              {estado === "confirmado" ? "Confirmar" : estado === "en_transito" ? "Despachar" : "Entregado"}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
